import {query} from "@/graphql/client";
import {
    AbsenceInput,
    AutopilotJobsiteSource,
    AutopilotPlayState, AutopilotPreferenceFragmentFragment,
    AutopilotStatus, AutopilotStatusFragmentFragment,
    Employee,
    PlanningStatusFragment,
    PlanningStatusPrecrimeFragment,
    WorktimeMode
} from "@/graphql/generated/graphql";
import date from "@/helper/datetime/date";
import {getIconForPlayState, getIconForWorktimeMode,} from "@/helper/autopilotPreference";
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {splitByJobsiteCategory} from "./jobsite";
import {kapitelMonthString} from "@/graphql/kapitelTypes";
import {graphql} from "@/graphql/generated";

export const autopilotStatusFragment = graphql(`fragment AutopilotStatusFragment on AutopilotStatus {
    month,
    employee {
        id
    },
    contractHours,
    sicknessHours
    plannedAbsences
    worktimeMode,
    worktimeModeModifierHours,
    bookingTargetHours,
    bookingTarget,
    booked,
    bookedWorkingHours,
    bookingHoursTargetReached,
    modus,
    offerStrategy,
    missingBookings,
    averageShiftDuration,
    missingBookingHours 
}`)

graphql(`fragment PlanningStatusShallow on PlanningStatus {
    month
    employee {
        id
    }
    planningMode
    bookingCompleted

    contractHours

    bookings
    bookedJobsites {
        ...JobsiteFragment
        client {
            ...ClientFragment
        }
        house {
            ...HouseFragment
        }
    }

    availabilities
    availabilityShifts

    vacations
}`)
graphql(`fragment PlanningStatusPrecrime on PlanningStatus {
    avgDemandPerMonth {
        jobsite {
            id,
            name
            abbreviation,
            client {
                name,
                abbreviation
            }
            house {
                name,
                abbreviation
            }
        }
        averageMonthlyDemand
        currentPrediction
    },
    predictedAdditionalBookings,
    shiftPortfolio
}`)



/**
 * plural
 *
 * @param sorted
 */
export async function fetchCurrentAutopilotStatus(sorted = true): Promise<AutopilotStatus[]> {
    const result = await query(graphql(`
        query getCurrentAutopilotStatus {
            currentAutopilotStatus {
                ...AutopilotStatusFragment
                autopilotPreferences {
                    ...AutopilotPreferenceFragment
                }
            }
        }`)
    )

    const autopilotStatus = result?.data?.currentAutopilotStatus || []

    return sorted
        ? autopilotStatus.sort(sorter)
        : autopilotStatus
}

export async function fetchCurrentPlanningStatus(sorted = true): Promise<PlanningStatusFragment[]> {
    const result = await query(graphql(`
        query getCurrentPlanningStatus {
            currentPlanningStatus {
                ...PlanningStatusShallow
                autopilotStatus {
                    ...AutopilotStatusFragment
                    autopilotPreferences {
                        ...AutopilotPreferenceFragment
                    }
                }
            }
        }`)
    )

    const planningStatus = result?.data?.currentPlanningStatus

    return sorted
        ? planningStatus.sort(sorter)
        : planningStatus
}


/**
 * singular
 */
export async function fetchAutopilotStatus(month: CalendarMonth | kapitelMonthString, employee: Employee | undefined = undefined): Promise<AutopilotStatus> {
    const result = await query(graphql(`
        query getAutopilotStatus($month: KapitelDateImmutable!, $employeeId: Int = null) {
            autopilotStatus(month: $month, employeeId: $employeeId) {
                ...AutopilotStatusFragment
                autopilotPreferences {
                    ...AutopilotPreferenceFragment
                }
            }
        }`),{
            month: typeof month === "string" ? month : month.begin,
            employeeId: employee?.id || null,
        }
    )

    return result?.data?.autopilotStatus

}

graphql(`fragment PlanningStatus on PlanningStatus {
    ...PlanningStatusShallow
    autopilotStatus {
        ...AutopilotStatusFragment
        autopilotPreferences {
            ...AutopilotPreferenceFragment
        }
    }
}`)
export async function fetchPlanningStatus(month: CalendarMonth, employee: Employee | undefined = undefined ): Promise<PlanningStatusFragment> {
    const result = await query(graphql(`
        query getPlanningStatus($month: KapitelDateImmutable!, $employeeId: Int = null) {
            planningStatus(month: $month, employeeId: $employeeId) {
                ...PlanningStatus
            }
        }`),{
            month: month.begin,
            employeeId: employee?.id || undefined,
        }
    )

    return result?.data?.planningStatus
}

export async function fetchPlanningStatusWithPrecrime(month: CalendarMonth, employee: Employee | undefined = undefined ): Promise<PlanningStatusFragment&PlanningStatusPrecrimeFragment> {
    const result = await query(graphql(`
        query getPlanningStatusWithPrecrime($month: KapitelDateImmutable!, $employeeId: Int = null) {
            planningStatus(month: $month, employeeId: $employeeId) {
                ...PlanningStatusShallow
                ...PlanningStatusPrecrime
                autopilotStatus {
                    ...AutopilotStatusFragment
                    autopilotPreferences {
                        ...AutopilotPreferenceFragment
                    }
                }
            }
        }`),{
            month: month.begin,
            employeeId: employee?.id || undefined,
        }
    )

    return result?.data?.planningStatus

}

export async function fetchCurrentPlanningStatusWithPrecrime(sorted = true): Promise<(PlanningStatusFragment&PlanningStatusPrecrimeFragment)[]> {
    const result = await query(graphql(`
        query getCurrentPlanningStatusWithPrecrime {
            currentPlanningStatus {
                ...PlanningStatusShallow
                ...PlanningStatusPrecrime
                autopilotStatus {
                    ...AutopilotStatusFragment
                    autopilotPreferences {
                        ...AutopilotPreferenceFragment
                    }
                }
            }
        }`)
    )

    const planningStatus = result?.data?.currentPlanningStatus

    return sorted
        ? planningStatus.sort(sorter)
        : planningStatus
}

export function sorter(a : AutopilotStatus|PlanningStatusFragment, b : AutopilotStatus|PlanningStatusFragment) {
    return date.cmpKapitelDate(a.month, b.month)
}

export function getAutopilotWorktimeModeIcon(status: {worktimeMode: WorktimeMode}) {
    return getIconForWorktimeMode(status.worktimeMode)
}

export function getAutopilotWorktimeModeLabel(worktimeMode: WorktimeMode) {
    switch (worktimeMode) {
        case WorktimeMode.Less:
            return 'Weniger arbeiten'
        case WorktimeMode.SpotOn:
            return 'Sollstunden erreichen'
        case WorktimeMode.More:
            return 'Überstunden ansammeln'
    }
}

export function getAutopilotWorktimeModeDescription(worktimeMode: WorktimeMode) {
    switch (worktimeMode) {
        case WorktimeMode.Less:
            return 'Das ist weniger als deine Sollstunden'
        case WorktimeMode.SpotOn:
            return 'Damit erreichst du deine Sollstunden'
        case WorktimeMode.More:
            return 'Damit sammelst du Überstunden an'
    }
}

export function getAutopilotStatusSummary(autopilotStatus: AutopilotStatusFragmentFragment & {autopilotPreferences: AutopilotPreferenceFragmentFragment} | undefined) {
    const autopilotPreference = autopilotStatus?.autopilotPreferences;

    const jobsites = autopilotPreference?.autopilotJobsites.map(apjs => apjs.jobsite) || [] 
    const {favorite, alternative, booked, other} = splitByJobsiteCategory(jobsites)

    const automaticallyAdded = autopilotPreference?.autopilotJobsites.filter(apjs => apjs.source == AutopilotJobsiteSource.Manually)
    const manuallyAdded = autopilotPreference?.autopilotJobsites.filter(apjs => apjs.source == AutopilotJobsiteSource.Favorite)

    const jobsiteSummary = {
        jobsites: jobsites,
        count: jobsites.length,

        favoriteCount: favorite.jobsites.length,
        favoriteOnly: jobsites.length === favorite.jobsites.length,
        alternativeCount: alternative.jobsites.length,
        remainingCount: booked.jobsites.length + other.jobsites.length,

        automaticallyAddedCount: automaticallyAdded?.length || 0,
        manuallyAddedCount: manuallyAdded?.length || 0
    }

    return {
        playState: autopilotPreference?.playState,
        playStateIcon: autopilotPreference ? getIconForPlayState(autopilotPreference?.playState) : undefined,
        statusIsRunning: autopilotPreference?.playState == AutopilotPlayState.Running,
        statusIsPaused: autopilotPreference?.playState == AutopilotPlayState.Paused,
        statusIsScrapped: autopilotPreference?.playState == AutopilotPlayState.Scrapped,
        jobsiteSummary,
        worktimeMode: autopilotStatus?.worktimeMode,
        worktimeIcon: autopilotStatus ? getAutopilotWorktimeModeIcon(autopilotStatus) : undefined,
        worktimeLabel: autopilotStatus ? getAutopilotWorktimeModeLabel(autopilotStatus.worktimeMode) : undefined,
        offerStrategy: autopilotStatus?.offerStrategy,
        booked: autopilotStatus?.booked,
        bookedWorkingHours: autopilotStatus?.bookedWorkingHours,
        bookingHoursTargetReached: autopilotStatus?.bookingHoursTargetReached,

        bookingTarget: autopilotStatus?.bookingTarget,
        bookingTargetHours: autopilotStatus?.bookingTargetHours,
        contractHours: autopilotStatus?.contractHours,
        missingBookingHours: autopilotStatus?.missingBookingHours,
        missingBookings: autopilotStatus?.missingBookings,

        plannedAbsenceCount: autopilotStatus?.plannedAbsences
    }
}


export async function fetchSpotOnBookingTarget(month: CalendarMonth, sicknesses: AbsenceInput[] | undefined = undefined ): Promise<number> {
    const result = await query(graphql(`
            query getSpotOnBookingTarget($month: KapitelDateImmutable!, $sicknesses: [AbsenceInput!]!) {
                spotOnBookingTarget(month: $month, sicknesses: $sicknesses)
            }`),{
            month: month.begin,
            sicknesses,
        }
    )

    return result?.data?.spotOnBookingTarget

}
